import {
  addDays,
  addMonths,
  addWeeks,
  getWeek,
  isSameDay,
  isSameMonth,
  isSameWeek,
  setDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns'
import getDaysInMonth from 'date-fns/getDaysInMonth'
import isValid from 'date-fns/isValid'
import { Variants } from 'framer-motion'
import range from 'lodash/range'
import { DayNum, MonthNum, WeekNum, WeekStartsOn } from '../types/dates'
import { isBetween } from '../util/helpers'

export const CURTAIN_VARIANTS: Variants = {
  visible: {
    opacity: 1,
    scaleY: 1,
    translateY: 0,
  },
  hidden(keepOpen: boolean) {
    return {
      opacity: keepOpen ? 1 : 0,
      scaleY: keepOpen ? 1 : 0.8,
      translateY: keepOpen ? 0 : -25,
    }
  },
}

const numbersOnly = (s: string) => /^[0-9]+$/.test(s)

function dateFromYMD(year: number, month: number, day: number) {
  if (!isBetween(month, 0, 11)) return new Date('')
  const date = new Date(year, month)
  const dim = getDaysInMonth(date)
  if (!isBetween(day, 1, dim)) return new Date('')

  return new Date(year, month, day)
}
function validateDate(date: Date) {
  return isValid(date) ? date : null
}

export function tryParseDate(input: string, fallback: Date): [Date, boolean] {
  const parse: () => [number, number, number] | Date | null = () => {
    const now = new Date()

    // If there's only numbers, there's a limited set of options
    if (numbersOnly(input)) {
      const parsed = parseInt(input, 10)

      // It's probably a full year
      if (input.length === 4) return [parsed, 0, 1]

      // It's probably a shortened year
      if (input.length === 2) {
        // Smaller than or equal to current year is probably meant as 20XX
        if (parsed <= parseInt(('' + now.getFullYear()).slice(-2), 10))
          return [2000 + parsed, 0, 1]
        // Otherwise, 19XX
        else return [1900 + parsed, 0, 1]
      }

      // Is it of format 090193?
      if (input.length === 6) {
        {
          const day = parseInt(input.substring(0, 2), 10)
          const month = parseInt(input.substring(2, 4), 10) - 1
          const year = parseInt(input.substring(4), 10) + 1900

          const validated = validateDate(dateFromYMD(year, month, day))
          if (validated !== null) return validated
        }

        // Okay, try 930109 instead
        {
          const year = parseInt(input.substring(0, 2), 10) + 1900
          const month = parseInt(input.substring(2, 4), 10) - 1
          const day = parseInt(input.substring(4), 10)

          return [year, month, day]
        }
      }

      // Is it of format 09011993?
      if (input.length === 8) {
        {
          const day = parseInt(input.substring(0, 2), 10)
          const month = parseInt(input.substring(2, 4), 10) - 1
          const year = parseInt(input.substring(4), 10)

          const validated = validateDate(dateFromYMD(year, month, day))
          if (validated !== null) return validated
        }

        // Okay, try 19930109 instead
        {
          const year = parseInt(input.substring(0, 4), 10)
          const month = parseInt(input.substring(4, 6), 10) - 1
          const day = parseInt(input.substring(6), 10)

          return [year, month, day]
        }
      }
    }

    // Okay, there's more than numbers here, let's try some separators
    const re = /(-|\/|\.|\s)/
    let separated: string[] = []
    const split = input.split(re)
    if (split.length > 1) {
      separated = split.reduce<string[]>(
        (acc, cur) => (numbersOnly(cur) ? [...acc, cur] : acc),
        []
      )
    }

    // Nope, must be three long
    if (separated.length !== 3) return null

    // We gotta figure out what's the year here
    if (separated[0].length === 4) {
      // First one is year, gonna go yyyy-MM-dd
      const [year, month, day] = separated.map(s => parseInt(s, 10))

      // Gotta check if user has a clue
      if (!isBetween(month, 1, 12)) return null
      if (!isBetween(day, 1, 31)) return null

      return [year, month - 1, day]
    } else if (separated[2].length === 4) {
      // Last one must be year, then
      const [day, month, year] = separated.map(s => parseInt(s, 10))

      // Gotta check if user has a clue again
      if (!isBetween(month, 1, 12)) return null
      if (!isBetween(day, 1, 31)) return null

      return [year, month - 1, day]
    }

    return null
  }

  const parsed = parse()
  let validated: Date | null = null
  if (parsed !== null) {
    if (parsed instanceof Date) validated = validateDate(parsed)
    else validated = validateDate(dateFromYMD(...parsed))
  }

  return [validated ?? fallback, validated !== null]
}

export interface Day {
  num: DayNum
  initial: string
  veryShortName: string
  shortName: string
  longName: string
  isToday: boolean
  date: Date
}
type WeekDays = [Day, Day, Day, Day, Day, Day, Day]
interface Week {
  num: WeekNum
  isThisWeek: boolean
  days: WeekDays
}
interface Month {
  num: MonthNum
  year: number
  initial: string
  shortName: string
  longName: string
  isThisMonth: boolean
  weeks: Week[]
}

type FormatFn = (date: Date, format: string) => string
export enum DayNameFormat {
  Initial = 'ccccc',
  VeryShort = 'cccccc',
  Short = 'ccc',
  Long = 'cccc',
}
export enum MonthNameFormat {
  Initial = 'LLLLL',
  Short = 'LLL',
  Long = 'LLLL',
}

function resolveWeek(
  date: Date,
  formatter: FormatFn,
  weekStartsOn: WeekStartsOn
): Week {
  const now = new Date()
  const firstDateInWeek = startOfWeek(date, { weekStartsOn })

  const returnData: Week = {
    num: getWeek(firstDateInWeek) as WeekNum,
    isThisWeek: isSameWeek(firstDateInWeek, now),
    days: range(7).map<Day>(i => {
      const day = addDays(firstDateInWeek, i)
      return {
        num: day.getDate() as DayNum,
        initial: formatter(day, DayNameFormat.Initial),
        veryShortName: formatter(day, DayNameFormat.VeryShort),
        shortName: formatter(day, DayNameFormat.Short),
        longName: formatter(day, DayNameFormat.Long),
        isToday: isSameDay(day, now),
        date: day,
      }
    }) as WeekDays,
  }
  return returnData
}
export function resolveMonth(
  date: Date,
  formatter: FormatFn,
  weekStartsOn: WeekStartsOn
): Month {
  const now = new Date()
  const firstDateInMonth = startOfMonth(date)
  let currentDate = setDay(firstDateInMonth, weekStartsOn, { weekStartsOn })

  const returnData: Month = {
    num: firstDateInMonth.getMonth() as MonthNum,
    year: firstDateInMonth.getFullYear(),
    initial: formatter(firstDateInMonth, MonthNameFormat.Initial),
    shortName: formatter(firstDateInMonth, MonthNameFormat.Short),
    longName: formatter(firstDateInMonth, MonthNameFormat.Long),
    isThisMonth: isSameMonth(now, firstDateInMonth),
    weeks: [],
  }

  do {
    returnData.weeks.push(resolveWeek(currentDate, formatter, weekStartsOn))
    currentDate = addWeeks(currentDate, 1)
  } while (currentDate.getMonth() === date.getMonth())

  return returnData
}

export function getDayNames(
  format: DayNameFormat,
  formatter: FormatFn,
  weekStartsOn: WeekStartsOn
) {
  const firstDayOfWeek = startOfWeek(new Date(), { weekStartsOn })
  return range(7).map(i => formatter(addDays(firstDayOfWeek, i), format))
}

export function getMonthNames(format: MonthNameFormat, formatter: FormatFn) {
  const firstDayOfYear = startOfYear(new Date())
  return range(12).map(i => formatter(addMonths(firstDayOfYear, i), format))
}
